import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sessions as SessionsAPI, Billing, BillingEmail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LocalStore, buildFilename } from "../lib/storage";
import { Plus, ArrowRight, Trash, Camera as CameraIcon, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { QueueBanner } from "../components/QueueBanner";
import { PostCaptureNudge } from "../components/PostCaptureNudge";
import { CaptureMoment } from "../lib/captureMoment";

const relTime = (iso) => {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(LocalStore.getActiveSession());
  const [nudgePayload, setNudgePayload] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();

  const reload = async () => {
    setLoading(true);
    try {
      const data = await SessionsAPI.list();
      setSessions(data);
    } catch (e) {
      toast.error("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // iter-36 — re-fetch when auth identity flips (sign-in / sign-out)
    // so the list swaps between "MINE" and "GUEST" scope without a manual
    // refresh. `user?.user_id` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Post-capture conversion nudge (iter-18). Only render if:
  //   - User just exited Camera with >0 captures
  //   - User is NOT already paid
  //   - User did NOT dismiss within the 24h cooldown
  useEffect(() => {
    const payload = CaptureMoment.readPending();
    if (!payload) return;
    if (CaptureMoment.isDismissedRecently() || user?.sync_enabled) {
      CaptureMoment.clearPending();
      return;
    }
    const cachedEmail = BillingEmail.get();
    if (!cachedEmail) {
      setNudgePayload(payload);
      return;
    }
    // Check billing status — if already paid, suppress and clear.
    (async () => {
      try {
        const r = await Billing.status(cachedEmail);
        if (r.sync_enabled) {
          CaptureMoment.clearPending();
        } else {
          setNudgePayload(payload);
        }
      } catch {
        // Network error — show the nudge anyway (better to over-prompt
        // than to silently skip a real conversion moment).
        setNudgePayload(payload);
      }
    })();
  }, []);

  // Post-checkout polling (iter-17). When Stripe redirects back here with
  // ?upgraded=1&session_id=XYZ, poll the backend until the payment is
  // confirmed (or we time out). Backend's polling endpoint is the canonical
  // source of truth — webhook is just there for durability/race-coverage.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") !== "1") return;
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await Billing.checkoutStatus(sessionId);
        if (r.payment_status === "paid") {
          if (r.email) BillingEmail.set(r.email);
          toast.success("SYNC UNLOCKED. WELCOME TO PRO.");
          // Strip the upgrade params from the URL.
          window.history.replaceState({}, "", "/sessions");
          return;
        }
        if (r.status === "expired") {
          toast.error("CHECKOUT EXPIRED. TRY AGAIN.");
          window.history.replaceState({}, "", "/sessions");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          toast("PAYMENT PROCESSING. CHECK YOUR EMAIL FOR CONFIRMATION.");
          window.history.replaceState({}, "", "/sessions");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        // Network blip — retry up to the cap.
        attempts += 1;
        if (attempts < maxAttempts) setTimeout(tick, 2000);
      }
    };
    toast("CONFIRMING YOUR PAYMENT…");
    tick();
    return () => { cancelled = true; };
  }, []);

  const handleActivate = (s) => {
    LocalStore.setActiveSession(s);
    setActive(s);
    toast.success(`Active: ${s.name}`);
    nav("/camera");
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete session "${s.name}" and all its photos?`)) return;
    try {
      await SessionsAPI.remove(s.id);
      if (active?.id === s.id) {
        LocalStore.setActiveSession(null);
        setActive(null);
      }
      toast.success("Session deleted");
      reload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto page-fade" data-testid="sessions-page">
      {nudgePayload && (
        <PostCaptureNudge
          payload={nudgePayload}
          onClose={() => setNudgePayload(null)}
          onUpgraded={() => setNudgePayload(null)}
        />
      )}
      <QueueBanner />
      <div className="flex items-end justify-between mb-6 gap-3">
        <div className="min-w-0">
          <p className="label-mono text-neutral-500" data-testid="sessions-scope-label">
            01 / SESSIONS {user ? "· MINE" : "· GUEST"}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            {user ? "MY SESSIONS" : "ALL SESSIONS"}
          </h1>
          <p className="text-xs font-mono text-neutral-500 mt-1">
            {sessions.length} TOTAL · {sessions.reduce((a, s) => a + (s.photo_count || 0), 0)} FRAMES
            {user && (
              <> · <span data-testid="sessions-owner-email" title={user.email}>{user.email}</span></>
            )}
          </p>
        </div>
        <button
          onClick={() => nav("/setup")}
          className="btn-flat shrink-0"
          data-testid="new-session-btn"
        >
          <Plus weight="bold" size={18} /> New
        </button>
      </div>

      {active && (
        <div
          className="hairline-2 bg-black text-white p-4 mb-6 flex items-center justify-between gap-3"
          data-testid="active-session-banner"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="chip chip-live">
                <span className="w-1.5 h-1.5 bg-white live-dot" />
                LIVE
              </span>
              <span className="label-mono opacity-70">ACTIVE</span>
            </div>
            <p className="font-display text-xl font-bold truncate">{active.name}</p>
            <p className="text-xs font-mono opacity-80 truncate">
              NEXT → {buildFilename(active.name, active.base_tags, active.current_sequence)}
            </p>
          </div>
          <button
            onClick={() => nav("/camera")}
            className="btn-flat-outline bg-white text-black shrink-0"
            data-testid="resume-capture-btn"
          >
            <CameraIcon weight="bold" size={18} /> Capture
          </button>
        </div>
      )}

      {loading ? (
        <ul className="space-y-3" data-testid="sessions-skeleton">
          {[0, 1, 2].map((i) => (
            <li key={i} className="hairline-2 p-4">
              <div className="h-5 w-2/3 skel mb-2" />
              <div className="h-3 w-1/2 skel mb-2" />
              <div className="h-3 w-1/3 skel" />
            </li>
          ))}
        </ul>
      ) : sessions.length === 0 ? (
        <div
          className="hairline-2 p-8 sm:p-14 text-center bg-white"
          data-testid="empty-sessions"
          style={{ boxShadow: "6px 6px 0 0 #0A0A0A" }}
        >
          <p className="label-mono text-neutral-500 mb-3">
            01 / WELCOME
          </p>
          <p
            className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl mb-4"
            data-testid="empty-sessions-headline"
          >
            CREATE YOUR
            <br />
            FIRST SESSION
          </p>
          <p className="text-sm sm:text-base font-mono text-neutral-600 max-w-md mx-auto mb-8">
            Name it, give it a tag, and your shutter is live. No settings to fiddle with.
          </p>
          <button
            onClick={() => nav("/setup")}
            className="btn-flat text-base px-6 py-4 inline-flex items-center gap-3"
            data-testid="create-first-session-btn"
            style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}
          >
            <Plus weight="bold" size={20} /> Create Session
            <ArrowRight weight="bold" size={18} />
          </button>
          <p className="label-mono text-neutral-400 mt-6 text-[10px]">
            FILES AUTO-NAMED <span className="text-black">[NAME]_[TAG]_[#].JPG</span> · EXIF BAKED
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {sessions.map((s) => {
            const isActive = active?.id === s.id;
            return (
              <li
                key={s.id}
                className={`hairline-2 p-4 flex items-center justify-between gap-3 transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                  isActive ? "bg-black text-white" : "bg-white"
                }`}
                style={{
                  boxShadow: isActive ? "4px 4px 0 0 #FF2A2A" : "4px 4px 0 0 #0A0A0A",
                }}
                data-testid={`session-row-${s.id}`}
              >
                <button
                  onClick={() => handleActivate(s)}
                  className="flex-1 text-left min-w-0"
                  data-testid={`activate-session-${s.id}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {isActive && <span className="chip chip-live">ACTIVE</span>}
                    <span className={`label-mono ${isActive ? "opacity-70" : "text-neutral-500"}`}>
                      <Clock size={11} weight="bold" className="inline -mt-0.5 mr-1" />
                      {relTime(s.updated_at)}
                    </span>
                  </div>
                  <p className="font-display text-xl font-bold leading-tight truncate">
                    {s.name}
                  </p>
                  <p className={`text-sm font-mono truncate ${isActive ? "opacity-80" : "text-neutral-600"}`}>
                    {s.base_tags}
                  </p>
                  <p className={`label-mono mt-2 ${isActive ? "opacity-70" : "text-neutral-500"}`}>
                    <span className={`inline-block px-1.5 py-0.5 mr-2 ${isActive ? "bg-white text-black" : "bg-black text-white"}`}>
                      {s.photo_count} PHOTOS
                    </span>
                    NEXT #{String(s.current_sequence).padStart(3, "0")}
                  </p>
                </button>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    onClick={() => handleActivate(s)}
                    className="w-11 h-11 border-2 border-current flex items-center justify-center transition-transform active:scale-90"
                    data-testid={`open-session-${s.id}`}
                    aria-label="Open"
                  >
                    <ArrowRight weight="bold" size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(s)}
                    className={`w-11 h-11 border-2 flex items-center justify-center transition-transform active:scale-90 ${
                      isActive ? "border-white" : "border-black hover:bg-black hover:text-white"
                    }`}
                    data-testid={`delete-session-${s.id}`}
                    aria-label="Delete"
                  >
                    <Trash weight="bold" size={18} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
